import { useContext, useEffect, useState } from 'react';
import { ChevronRight, GitBranch, Search } from 'react-feather';
import { PortfolioManagerContext } from './PortfolioManagerPage';
import { PAGES, ROUTE_PATHS } from '../../../InternalApiApp';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import DetailSectionHeader from '../../Shared/DetailSection/DetailSectionHeader';
import SimpleSearchFilter, { filterBy } from '../../../components/Input/SimpleSearchFilter';
import Table from '../../../components/Table/Table';
import useWindowDimensions, { useStorage } from '../../../Utils/Utils';
import DynamicDetails from '../SchemesAndFunds/DynamicDetails';
import PageContentToggle from '../../../components/Menus/PageContentToggle';
import TruncatedString from '../../Shared/TruncatedString';
import Tooltip from '../../../components/popups/Tooltip';

// hierachy endpoint provides the hierarchy of groupings
// each grouping has a hierarchyOrder, groupingName, groupingId, displaysInHierarchy, groups (with properties badgedParent etc)	
// The right side of the page should have 2 sections: Hierarchy and Badged Versions
// Hierarchy should show the inmediate previous and next groupings in the hierarchy (if any). Toggle tabs to display a table of the previous grouping (with links to each group) or a table of the next grouping (with links to each group).
// Badge versions are "sister groups of the current group."

const PortfolioManagerPageGeneral = () => {
	const { config, staticData, setStaticData, editMode, list, searchTerm, setSearchTerm, groupName, id, actualGroup } = useContext(PortfolioManagerContext)
	const { width } = useWindowDimensions()
	const storage = useStorage();

	const [hierarchy, setHierarchy] = useState(null);
	const [hierarchyTabs, setHierarchyTabs] = useState([])
	const [hierarchyTab, setHierarchyTab] = useState(null);
	const [groupsInGroupings, setGroupsInGroupings] = useState([]);
	const [badgedVersions, setBadgedVersions] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		storage.getOrFetch('funds/portfolio/groupings/hierarchy').then((fetchedHierarchy) => {
			const sortedHierarchy = fetchedHierarchy.sort((a, b) => a.hierarchyOrder - b.hierarchyOrder)//.filter(h => {});
			const lastGroupInHierarchy = sortedHierarchy[sortedHierarchy.length - 1]
			const isLast = lastGroupInHierarchy.groupingName === groupName;

			const filteredGroupings = list.map((item) => item.groupings).flatMap(Object.entries).reduce((acc, [key, value]) => {
				acc[key] = acc[key] ? [...new Set([...acc[key], value])] : [value];
				return acc;
			}, {});

			delete filteredGroupings[groupName]; // remove current grouping
			const groupNameIndex = sortedHierarchy.findIndex(item => item.groupingName === groupName);

			const filteredTabs = sortedHierarchy.map((item) => { // remove groupings that are not related to the current group eg. a manager that belongs to no practices
				return item.groupingName
			}).filter((tab, index) => {
				return (index === groupNameIndex - 1 || index === groupNameIndex + 1) && filteredGroupings[tab];
			});

			const tabs = isLast ? [...filteredTabs, 'Portfolio'] : filteredTabs;

			const actualBadgedVersions = fetchedHierarchy.flatMap(h => h.groups)
				.filter((item) => item?.properties?.badgedParent == actualGroup?.groupId);

			setHierarchy(fetchedHierarchy.reduce((map, item) => { map[item.groupingName] = item; return map }, {}));
			setGroupsInGroupings(filteredGroupings);
			setHierarchyTabs(tabs);
			setHierarchyTab(tabs[0]);
			setBadgedVersions(actualBadgedVersions);
			setLoading(false);
		});
	}, [groupName, list, id, actualGroup]);

	function updateStaticData(key, value) {
		// save temporary updates
		let updatedArr = []
		if (staticData.find(e => e.key === key)) {
			updatedArr = staticData.map(e => {
				if (e.key === key) {
					return { ...e, value: value }
				} else return e
			})
		} else {
			updatedArr = [...staticData, { key, value }]
		}
		setStaticData(updatedArr)
	}

	function filterData(dataToFilter, arr) {
		if (searchTerm === null || searchTerm.trim().size === 0) {
			return dataToFilter
		}
		return dataToFilter.filter((datum) => filterBy(datum, arr, searchTerm))
	}


	return (hierarchy &&
		<>
			<PageGridItem container col="1 / span 6" direction='col' gap="xl" style={{ display: 'flex', paddingRight: '1.5rem' }}>
				<PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid>

					<PageGridItem container col="1 / span 6" >
						<DynamicDetails
							editMode={editMode}
							staticData={staticData}
							config={config}
							updateStaticData={updateStaticData}
							actualGroup={actualGroup}
							name={groupName}
							group={config && config.find(g => g.groupName === "General")}
						/>
					</PageGridItem>
				</PageContentBodyGrid>
			</PageGridItem>
			<PageGridItem container col="7 / span 6">
				{!loading &&
					<PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid divider={width < 1200 ? null : 'left'} >
						<PageGridItem container col="1 / span 6" >
							<PageGridItem container col="1 / span 6">
								<DetailSectionHeader header={"Hierarchy"} icon={<GitBranch />} />
							</PageGridItem>
							<PageGridItem col="1 / span 6" className="pt-m pb-m" direction="row" justifyContent="between" alignItems="end">
								<PageContentToggle labels={hierarchyTabs.map(tab => {
									return { label: tab, active: tab === hierarchyTab, onClick: () => setHierarchyTab(tab) }
								})} />
								<SimpleSearchFilter // TODO: update depending on the tab selected
									placeholder={`Search ${hierarchyTab}`}
									setSearchTerm={setSearchTerm}
									size="small"
									width="m"
									icon={<Search />}
									searchTerm={searchTerm}
								/>
							</PageGridItem>
							<PageGridItem col="1 / span 6">
								{hierarchy && hierarchyTab ?
									hierarchyTab === 'Portfolio'
										? <PortfolioTable key={hierarchyTab} data={list} filterData={filterData} />
										: <HierarchyTable key={hierarchyTab} groupsInGroupings={groupsInGroupings[hierarchyTab]} grouping={hierarchy[hierarchyTab]} filterData={filterData} />
									: ""
								}
							</PageGridItem>
							<BadgedVersions badgedVersions={badgedVersions} hierarchyTabs={hierarchyTabs} />
						</PageGridItem>
					</PageContentBodyGrid>
				}
			</PageGridItem>
		</>
	);
}

export default PortfolioManagerPageGeneral;


const PortfolioTable = ({ data, filterData,  }) => {
	const arr = ['fundName', 'fundNumber']
	const colWidths = ['auto', "32px"]
	const filteredData = filterData(data, arr)
	const history = useHistory();

	const dataMap = filteredData && filteredData.sort((a1, a2) => a1?.fundName?.localeCompare(a2?.fundName)).map((fund, idx) => {
		const { fundNumber, fundName, cloneOf } = fund
		const path = `${ROUTE_PATHS[PAGES.ADMIN]}/portfolio-management/portfolios/${fundNumber}`;

		function maxWidthFunction(parent, element) {
			if (parent) {
				const childrenWidth = 172 + element.offsetWidth + 8 + 16;
				return childrenWidth >= parent.offsetWidth;
			}
			return null;
		}
		return (
			<tr className='cursor-pointer portfolio-mgmt-table-row' style={idx === 0 ? { borderBottom: '1px solid #E7EAF2', borderTop: '1px solid #E7EAF2' } : { borderBottom: '1px solid #E7EAF2' }} onClick={() => history.push(path)}>
				<td style={{ display: 'flex', alignItems: 'center', gap: '8px', height: 52, borderBottom: 'none', borderTop: 'none' }}>
					<TruncatedString string={fundName} maxWidthFunction={(el1, el2) => maxWidthFunction(el1, el2)} />
					<div id={`portfolio-fund-number-container-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '172px' }}>
						({fundNumber})
						{cloneOf ? <i className='material-symbols-outlined' title="Badged Portfolio" style={{ verticalAlign: 'middle', color: '#06467D' }}>license</i> : ""}</div>
				</td>
				<td className='text-align-right' style={{ height: 52, borderBottom: 'none', borderTop: 'none' }}>
					<ChevronRight className='offer-page-table-chevron' size={16} />
				</td>
			</tr>
		)
	})

	return <Table
		minWidth={350}
		tableStyle={{ maxWidth: 1675 }}
		colWidths={colWidths}
		dataMap={dataMap}
		id='scheme-or-platform-list-of-offers-or-products'
	/>
}

const HierarchyTable = ({ groupsInGroupings, grouping, filterData }) => {
	const history = useHistory();
	const colWidths = ['auto', "32px"]
	const keyName = grouping.groupingName;
	const subRoute = (keyName.toLowerCase() + 's/');
	const path = `${ROUTE_PATHS[PAGES.ADMIN]}/portfolio-management/${subRoute}`;
	const groups = grouping.groups.reduce((acc, group) => {
		acc[group.groupName] = { groupId: group.groupId, groupProperties: group.properties };
		return acc;
	}, {})

	const formattedData = groupsInGroupings.map(g => { return { name: g } }).sort((a, b) => a.name.localeCompare(b.name));
	const dataToUse = filterData(formattedData, ["name"])

	const dataMap = dataToUse && dataToUse.map((group, vIdx) => {
		const value = group.name
		const groupDetails = groups[value]
		const groupId = groupDetails ? groupDetails.groupId : null
		const properties = groupDetails ? groupDetails.groupProperties : null

		return (
			<tr className='cursor-pointer portfolio-mgmt-table-row' onClick={() => history.push(path + groupId)}>
				<td>
					{value}
					{properties?.badged
						? <Tooltip label={`Badged ${keyName}`}>
							<i className='material-symbols-outlined' style={{ verticalAlign: 'middle', color: '#06467D' }}>license</i>
						</Tooltip>
						//<i className='material-symbols-outlined' title="Badged Group" style={{ verticalAlign: 'middle', color: '#06467D' }}>license</i>
						: properties?.selfLicensed
							? <Tooltip label={`Self Licensed`}><i className='material-symbols-outlined' style={{ verticalAlign: 'middle', color: '#996633' }}>license</i></Tooltip>
							: ""
					}
				</td>
				<td className='text-align-right'>
					<ChevronRight className='offer-page-table-chevron' size={16} />
				</td>
			</tr>
		)
	})
	return <>
		<Table
			minWidth={350}
			tableStyle={{ maxWidth: 1675 }}
			colWidths={colWidths}
			dataMap={dataMap}
			id='hierarchy-table'
		/>
	</>
}


const BadgedVersions = ({ badgedVersions, hierarchyTabs }) => {
	const nextLevel = hierarchyTabs && hierarchyTabs.length > 0 ? hierarchyTabs[hierarchyTabs.length - 1] : null
	const colWidths = ['auto']
	const history = useHistory();

	if (!badgedVersions || badgedVersions.length === 0) {
		return null;
	}

	const dataMap = badgedVersions && badgedVersions.map((group, idx) => {
		const { groupId, groupName } = group
		const subRoute = nextLevel && (nextLevel.toLowerCase() + 's/');
		const path = `${ROUTE_PATHS[PAGES.ADMIN]}/portfolio-management/${subRoute}${groupId}`;
		return (
			<tr key={idx} className='cursor-pointer portfolio-mgmt-table-row' >
				<td onClick={() => history.push(path)}>{groupName}</td>
			</tr>
		);
	})

	return <>
		<PageGridItem container col="1 / span 6" className="pt-xl pb-m">
			<DetailSectionHeader header={"Badged Versions"} icon={<i className='material-symbols-outlined' title="Badged Practice" style={{ verticalAlign: 'middle', color: '#06467D' }}>license</i>} />
		</PageGridItem>
		<PageGridItem col="1 / span 6">
			<Table
				minWidth={350}
				tableStyle={{ maxWidth: 1675 }}
				colWidths={colWidths}
				dataMap={dataMap}
				id='badged-versions-table'
			/>
		</PageGridItem>
	</>
}